import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Play, ExternalLink, X, Heart, MessageCircle, Send, ChevronLeft, ChevronRight } from "lucide-react";
import Seo from "../components/common/Seo";
import { ArrowIcon } from "../components/common/Section";
import { useI18n } from "../lib/i18n";
import { useSiteSettings } from "../lib/settings";
import { resolveImage } from "../components/admin/ImageUpload";
import api from "../lib/api";

/* ================================================================
 * INSTAGRAM-STYLE MEDIA CENTER
 * Top: Story Rings (per category) — tap to open full-screen story viewer
 * Below: Vertical post feed (image, video/reel, press) — Instagram-post cards
 * ================================================================ */

/* Category rings — keys are stable; labels come from Site Settings so the
   admin can rename them ("Zirve" → "Etkinlik", etc.) without touching code. */
const CATEGORY_RINGS = {
  news:        "from-[#22346B] via-[#4E6BC7] to-[#22346B]",
  video:       "from-[#c9184a] via-[#ff4d6d] to-[#8338ec]",
  event:       "from-[#ff9500] via-[#ffb400] to-[#ff5e00]",
  blog:        "from-[#38b6ff] via-[#3a86ff] to-[#22346B]",
  press:       "from-[#8B8D93] via-[#22346B] to-[#101F4A]",
  publication: "from-[#22346B] via-[#8338ec] to-[#22346B]",
};

const DEFAULT_LABELS = {
  news: "Haberler",
  video: "Video",
  event: "Etkinlik",
  blog: "Blog",
  press: "Basında Biz",
  publication: "Yayınlar",
};

const useCategoryMeta = () => {
  const s = useSiteSettings();
  const overrides = Array.isArray(s?.media_categories) ? s.media_categories : [];
  const labelByKey = { ...DEFAULT_LABELS };
  overrides.forEach((m) => {
    if (m && m.key && m.label) labelByKey[m.key] = m.label;
  });
  const order = overrides.length
    ? overrides.map((m) => m.key).filter((k) => k in CATEGORY_RINGS)
    : Object.keys(DEFAULT_LABELS);
  return order.map((key) => ({ key, label: labelByKey[key], ring: CATEGORY_RINGS[key] }));
};

const formatDate = (iso, lang) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(lang === "tr" ? "tr-TR" : "en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return ""; }
};

const timeAgo = (iso, lang) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor(diff / 3600000);
  const m = Math.floor(diff / 60000);
  const suf = lang === "tr" ? { d: "g", h: "s", m: "dk", ago: "önce" } : { d: "d", h: "h", m: "m", ago: "ago" };
  if (d >= 7) return formatDate(iso, lang);
  if (d >= 1) return `${d}${suf.d} ${suf.ago}`;
  if (h >= 1) return `${h}${suf.h} ${suf.ago}`;
  if (m >= 1) return `${m}${suf.m} ${suf.ago}`;
  return lang === "tr" ? "az önce" : "just now";
};

const catLabel = (key, meta) => {
  if (Array.isArray(meta)) {
    const found = meta.find((x) => x.key === key);
    if (found?.label) return found.label;
  }
  return DEFAULT_LABELS[key] || key;
};

/* --------------------------------------------------
 * Story Rings — horizontal circular strip
 * -------------------------------------------------- */
const StoryRings = ({ groups, onOpen, lang }) => {
  const meta = useCategoryMeta();
  const rings = meta
    .map((m) => ({ ...m, items: (groups[m.key] || []) }))
    .filter((r) => r.items.length > 0);
  if (rings.length === 0) return null;
  return (
    <div className="border-b border-black/5 bg-white" data-testid="media-stories">
      <div className="max-w-[970px] mx-auto px-4 md:px-8 py-4 md:py-5">
        <div className="flex gap-4 md:gap-6 overflow-x-auto scrollbar-thin -mx-2 px-2">
          {rings.map((r) => (
            <button
              type="button"
              key={r.key}
              onClick={() => onOpen(r.key)}
              className="flex flex-col items-center flex-shrink-0 group focus:outline-none"
              data-testid={`story-ring-${r.key}`}
            >
              <span className={`p-[2.5px] rounded-full bg-gradient-to-tr ${r.ring}`}>
                <span className="block p-[2px] rounded-full bg-white">
                  <span className="block w-16 h-16 md:w-20 md:h-20 rounded-full overflow-hidden bg-brand-navy">
                    <img
                      src={resolveImage(r.items[0].cover_image)}
                      alt=""
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </span>
                </span>
              </span>
              <span className="mt-2 text-[0.66rem] md:text-xs text-brand-navy font-medium max-w-[80px] text-center truncate">
                {r.label}
              </span>
              <span className="text-[0.55rem] tracking-[0.16em] uppercase text-brand-medium">
                {r.items.length}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/* --------------------------------------------------
 * Story Viewer — full-screen modal, auto-advance
 * -------------------------------------------------- */
const StoryViewer = ({ items, startIndex = 0, category, onClose, lang }) => {
  const [idx, setIdx] = useState(startIndex);
  const [progress, setProgress] = useState(0);
  const timer = useRef(null);
  const paused = useRef(false);

  const item = items[idx];

  useEffect(() => {
    setProgress(0);
    if (timer.current) clearInterval(timer.current);
    timer.current = setInterval(() => {
      if (paused.current) return;
      setProgress((p) => {
        if (p >= 100) {
          if (idx + 1 < items.length) {
            setIdx(idx + 1);
            return 0;
          }
          clearInterval(timer.current);
          onClose();
          return 100;
        }
        return p + 2; // ~5s per story (2 per 100ms)
      });
    }, 100);
    return () => timer.current && clearInterval(timer.current);
     
  }, [idx]);

  const next = () => idx + 1 < items.length ? (setIdx(idx + 1), setProgress(0)) : onClose();
  const prev = () => idx > 0 ? (setIdx(idx - 1), setProgress(0)) : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
     
  }, [idx]);

  if (!item) return null;
  const isVideo = !!item.video_url || item.category === "video";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black flex items-center justify-center"
      data-testid="story-viewer"
      onMouseDown={() => { paused.current = true; }}
      onMouseUp={() => { paused.current = false; }}
      onTouchStart={() => { paused.current = true; }}
      onTouchEnd={() => { paused.current = false; }}
    >
      {/* Content — Instagram-style 9:16 aspect */}
      <div className="relative h-full aspect-[9/16] max-w-full max-h-[100dvh]" style={{ background: "#000" }}>
        <img
          src={resolveImage(item.cover_image)}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Top progress bars */}
        <div className="absolute top-2 left-2 right-2 flex gap-1 z-30">
          {items.map((_, i) => (
            <div key={i} className="h-[3px] flex-1 bg-white/25 rounded overflow-hidden">
              <div
                className="h-full bg-white transition-[width] ease-linear"
                style={{ width: i < idx ? "100%" : i === idx ? `${progress}%` : "0%" }}
              />
            </div>
          ))}
        </div>
        {/* Header */}
        <div className="absolute top-6 left-4 right-4 z-30 flex items-center justify-between text-white">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-white/15 backdrop-blur-md flex items-center justify-center text-[0.6rem] tracking-[0.24em] uppercase font-bold">
              {(catLabel(category) || "").slice(0, 1)}
            </span>
            <div className="leading-tight">
              <div className="text-sm font-medium">{catLabel(category)}</div>
              <div className="text-[0.65rem] text-white/70">{timeAgo(item.published_at, lang)}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-white/90 hover:text-white p-1" data-testid="story-close">
            <X size={22} strokeWidth={1.5} />
          </button>
        </div>
        {/* Bottom info */}
        <div className="absolute inset-x-0 bottom-0 p-5 pb-8 z-30 text-white bg-gradient-to-t from-black/80 via-black/40 to-transparent">
          <h3 className="font-serif text-xl md:text-2xl leading-tight">
            {item.title?.[lang] || item.title?.tr || ""}
          </h3>
          {(item.excerpt?.[lang] || item.excerpt?.tr) ? (
            <p className="mt-2 text-sm text-white/85 line-clamp-2">
              {item.excerpt?.[lang] || item.excerpt?.tr}
            </p>
          ) : null}
          <Link
            to={`/media/${item.slug}`}
            onClick={onClose}
            className="mt-4 inline-flex items-center gap-2 bg-white text-brand-navy px-4 py-2 text-[0.66rem] tracking-[0.24em] uppercase font-medium"
            data-testid="story-open-detail"
          >
            {isVideo ? "Videoyu İzle" : "Yazının Devamı"} <ArrowIcon />
          </Link>
        </div>
        {/* Left/Right tap zones */}
        <button onClick={prev} className="absolute inset-y-0 left-0 w-1/3 z-20" aria-label="Önceki" />
        <button onClick={next} className="absolute inset-y-0 right-0 w-1/3 z-20" aria-label="Sonraki" />
        {/* Nav arrows (desktop) */}
        <button onClick={prev} className="hidden md:flex absolute -left-14 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center">
          <ChevronLeft size={20} />
        </button>
        <button onClick={next} className="hidden md:flex absolute -right-14 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center">
          <ChevronRight size={20} />
        </button>
      </div>
    </motion.div>
  );
};

/* --------------------------------------------------
 * Grid tile — Instagram profile mini square. Clicks navigate to the
 * indexable blog-style detail page (/media/:slug).
 * -------------------------------------------------- */
const GridTile = ({ n, lang, delay = 0 }) => {
  const isReel = !!n.video_url || n.category === "video";
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay }}
      data-testid={`grid-tile-${n.slug}`}
    >
      <Link to={`/media/${n.slug}`} className="block group">
        <div className="relative aspect-square overflow-hidden bg-brand-navy/5">
          <img
            src={resolveImage(n.cover_image)}
            alt={n.title?.[lang] || n.title?.tr || ""}
            className="w-full h-full object-cover transition-transform duration-[700ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-105"
            loading="lazy"
          />
          {isReel ? (
            <span className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 backdrop-blur-sm text-white flex items-center justify-center pointer-events-none">
              <Play size={12} strokeWidth={2} fill="currentColor" />
            </span>
          ) : null}
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-4 md:gap-6 text-white font-semibold">
            <span className="inline-flex items-center gap-1.5">
              <Heart size={16} strokeWidth={2} fill="currentColor" />
              <span className="text-sm">{n.like_count || 0}</span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <MessageCircle size={16} strokeWidth={2} fill="currentColor" />
              <span className="text-sm">{n.comment_count || 0}</span>
            </span>
          </div>
        </div>
        <div className="mt-1.5 md:mt-2 px-0.5">
          <div className="text-[0.55rem] tracking-[0.22em] uppercase text-brand-primary font-medium truncate">
            {catLabel(n.category)}
          </div>
          <div className="mt-0.5 text-xs md:text-sm font-medium text-brand-navy leading-snug line-clamp-2 group-hover:text-brand-primary transition-colors">
            {n.title?.[lang] || n.title?.tr || ""}
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

/* --------------------------------------------------
 * Main page
 * -------------------------------------------------- */
export const MediaListPage = () => {
  const { pick, lang } = useI18n();
  const meta = useCategoryMeta();
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("");
  const [story, setStory] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get(`/public/news`)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoaded(true));
  }, []);

  const groups = useMemo(() => {
    const m = {};
    items.forEach((n) => { const k = n.category || "news"; if (!m[k]) m[k] = []; m[k].push(n); });
    Object.values(m).forEach((arr) => arr.sort((a, b) => (b.published_at || "").localeCompare(a.published_at || "")));
    return m;
  }, [items]);

  const feed = useMemo(() => {
    const list = category ? (groups[category] || []) : items;
    return [...list].sort((a, b) => (b.published_at || "").localeCompare(a.published_at || ""));
  }, [items, category, groups]);

  const openStory = (catKey) => {
    const catItems = groups[catKey] || [];
    if (catItems.length) setStory({ category: catKey, items: catItems });
  };

  return (
    <div className="bg-brand-light min-h-screen pt-[104px] md:pt-28" data-testid="media-list-page">
      <Seo fallbackTitle={lang === "tr" ? "Medya Merkezi" : lang === "ar" ? "المركز الإعلامي" : "Media Center"} />

      {/* Stories */}
      <StoryRings groups={groups} onOpen={openStory} lang={lang} />

      {/* Category chips — only shown when we actually have content */}
      {loaded && items.length > 0 ? (
        <div className="bg-white border-b border-black/5 sticky top-0 z-20">
        <div className="max-w-[970px] mx-auto px-4 md:px-8 py-2.5 flex items-center gap-1.5 overflow-x-auto scrollbar-thin">
          <button
            type="button"
            onClick={() => setCategory("")}
            className={`px-3.5 py-1.5 text-[0.62rem] tracking-[0.22em] uppercase transition-all rounded-full whitespace-nowrap ${category === "" ? "bg-brand-navy text-white" : "text-brand-navy hover:bg-brand-navy/5"}`}
            data-testid="media-cat-all"
          >Tümü</button>
          {meta.map((c) => {
            const count = (groups[c.key] || []).length;
            if (!count) return null;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => setCategory(c.key)}
                className={`px-3.5 py-1.5 text-[0.62rem] tracking-[0.22em] uppercase transition-all rounded-full whitespace-nowrap ${category === c.key ? "bg-brand-navy text-white" : "text-brand-navy hover:bg-brand-navy/5"}`}
                data-testid={`media-cat-${c.key}`}
              >{c.label}</button>
            );
          })}
        </div>
      </div>
      ) : null}

      {/* Instagram-profile grid (3 mobile / 4 tablet / 5 desktop) */}
      <div className="max-w-[970px] mx-auto px-1 md:px-4 py-2 md:py-6" data-testid="media-grid">
        {!loaded ? (
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 md:gap-3" data-testid="media-grid-loading">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="aspect-square bg-black/5 animate-pulse" />
            ))}
          </div>
        ) : feed.length === 0 ? (
          <div className="py-20 md:py-28 flex flex-col items-center text-center" data-testid="media-empty">
            <div className="w-16 h-16 rounded-full bg-brand-navy/5 flex items-center justify-center mb-6">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" className="text-brand-navy">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <path d="m21 15-5-5L5 21" />
              </svg>
            </div>
            <div className="text-[0.66rem] tracking-[0.32em] uppercase text-brand-medium">
              Yakında
            </div>
            <h3 className="mt-3 font-serif text-2xl md:text-3xl text-brand-navy">
              İçerikler hazırlanıyor
            </h3>
            <p className="mt-4 max-w-md text-brand-dark/70 leading-relaxed">
              Fırat Construction Yapı A.Ş.&apos;nin haberleri, etkinlikleri ve basın yansımaları çok yakında burada olacak. Bizi takipte kalın.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 items-center">
              <Link
                to="/"
                className="text-[0.7rem] tracking-[0.28em] uppercase bg-brand-navy hover:bg-brand-primary text-white px-6 py-3 transition-colors"
                data-testid="media-empty-home"
              >
                Ana Sayfaya Dön
              </Link>
              <Link
                to="/contact"
                className="text-[0.7rem] tracking-[0.28em] uppercase text-brand-navy hover:text-brand-primary underline underline-offset-4 decoration-brand-navy/30 hover:decoration-brand-navy transition-colors"
                data-testid="media-empty-contact"
              >
                Bize Ulaşın
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 md:gap-3">
            {feed.map((n, i) => (
              <GridTile key={n.id} n={n} lang={lang} delay={(i % 12) * 0.02} />
            ))}
          </div>
        )}
      </div>

      {/* Story viewer */}
      <AnimatePresence>
        {story ? (
          <StoryViewer items={story.items} category={story.category} lang={lang} onClose={() => setStory(null)} />
        ) : null}
      </AnimatePresence>

      <style>{`
        .scrollbar-thin::-webkit-scrollbar { height: 4px; width: 4px; }
        .scrollbar-thin::-webkit-scrollbar-thumb { background: rgba(34,52,107,0.15); border-radius: 2px; }
      `}</style>
    </div>
  );
};

/* --------------------------------------------------
 * News detail page — blog-style, SEO-indexable, with likes & comments
 * -------------------------------------------------- */
export const NewsDetailPage = () => {
  const { slug } = useParams();
  const { t, pick, lang } = useI18n();
  const [n, setN] = useState(null);
  const [comments, setComments] = useState([]);
  const [likeCount, setLikeCount] = useState(0);
  const [liked, setLiked] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", text: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/public/news/${slug}`).then((r) => {
      setN(r.data);
      setLikeCount(r.data?.like_count || 0);
      setLiked(localStorage.getItem(`liked:${slug}`) === "1");
    }).catch(() => setN(null));
    api.get(`/public/news/${slug}/comments`).then((r) => setComments(r.data || [])).catch(() => setComments([]));
  }, [slug]);

  const like = async () => {
    if (liked) return;
    setLiked(true);
    setLikeCount((c) => c + 1);
    localStorage.setItem(`liked:${slug}`, "1");
    try { await api.post(`/public/news/${slug}/like`); } catch { /* silent */ }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/public/news/${slug}/comments`, form);
      setComments((prev) => [...prev, res.data]);
      setForm({ name: "", email: "", text: "" });
    } catch (err) {
      /* noop — could show toast */
    } finally { setSubmitting(false); }
  };

  if (!n) return <div className="container-x pt-40 pb-20">…</div>;

  const publicUrl = typeof window !== "undefined" ? `${window.location.origin}/media/${n.slug}` : "";
  // JSON-LD Article for Google rich snippets
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": (n.category === "video") ? "VideoObject" : "NewsArticle",
    "headline": pick(n.title, ""),
    "description": pick(n.excerpt, ""),
    "image": resolveImage(n.cover_image),
    "datePublished": n.published_at,
    "dateModified": n.updated_at || n.published_at,
    "author": n.author ? { "@type": "Person", "name": n.author } : { "@type": "Organization", "name": "Fırat Construction Yapı A.Ş." },
    "publisher": {
      "@type": "Organization",
      "name": "Fırat Construction Yapı A.Ş.",
      "logo": { "@type": "ImageObject", "url": `${typeof window !== "undefined" ? window.location.origin : ""}/logo.png` },
    },
    "mainEntityOfPage": publicUrl,
    "articleSection": catLabel(n.category),
    "keywords": (n.tags || []).join(", "),
    "interactionStatistic": [
      { "@type": "InteractionCounter", "interactionType": "https://schema.org/LikeAction", "userInteractionCount": likeCount },
      { "@type": "InteractionCounter", "interactionType": "https://schema.org/CommentAction", "userInteractionCount": comments.length },
    ],
  };
  if (n.category === "video" && n.video_url) {
    jsonLd.contentUrl = n.video_url;
    jsonLd.thumbnailUrl = resolveImage(n.cover_image);
    jsonLd.uploadDate = n.published_at;
  }

  return (
    <div data-testid="news-detail-page" className="bg-white">
      <Seo
        seo={n.seo}
        fallbackTitle={pick(n.title)}
        fallbackDescription={pick(n.excerpt)}
        fallbackImage={n.cover_image}
        url={publicUrl}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          // Escape `<` so a stray "</script>" inside data cannot break out of the tag.
          __html: JSON.stringify(jsonLd).replace(/</g, "\\u003c"),
        }}
      />

      {/* Article header — title on white, then image, then excerpt (magazine style) */}
      <section className="container-x pt-[120px] md:pt-32 max-w-3xl">
        <div className="mb-5 flex items-center gap-3 flex-wrap">
          <span className="inline-block bg-brand-navy text-white text-[0.6rem] tracking-[0.28em] uppercase font-medium px-2.5 py-1">
            {catLabel(n.category)}
          </span>
          <span className="text-[0.66rem] tracking-[0.22em] uppercase text-brand-medium">
            {formatDate(n.published_at, lang)}
          </span>
          {n.author ? <span className="text-[0.66rem] tracking-[0.22em] uppercase text-brand-medium">· {n.author}</span> : null}
          {n.read_minutes ? <span className="text-[0.66rem] tracking-[0.22em] uppercase text-brand-medium">· {n.read_minutes} dk okuma</span> : null}
        </div>
        <h1 className="font-serif leading-[1.05] text-brand-navy" style={{ fontSize: "clamp(1.85rem, 4vw, 3.25rem)" }}>
          {pick(n.title, "")}
        </h1>
      </section>

      {/* Featured image — below title, above description */}
      <section className="container-x mt-8 md:mt-10 max-w-4xl">
        <figure className="relative w-full aspect-[16/9] overflow-hidden bg-brand-navy/5">
          <img
            src={resolveImage(n.cover_image)}
            alt={pick(n.title, "")}
            className="w-full h-full object-cover"
          />
          {n.video_url || n.category === "video" ? (
            <a
              href={n.video_url || "#"}
              target="_blank"
              rel="noreferrer"
              className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors"
              aria-label="Videoyu aç"
            >
              <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/95 text-brand-navy flex items-center justify-center shadow-2xl">
                <Play size={28} strokeWidth={1.5} fill="currentColor" />
              </span>
            </a>
          ) : null}
        </figure>
      </section>

      {/* Excerpt (description) + body */}
      <section className="container-x py-10 md:py-14 max-w-3xl">
        {(n.excerpt?.[lang] || n.excerpt?.tr) ? (
          <p className="font-serif text-lg md:text-xl text-brand-dark leading-relaxed mb-8">
            {n.excerpt?.[lang] || n.excerpt?.tr}
          </p>
        ) : null}

        {/* Actions bar */}
        <div className="flex items-center gap-4 pb-5 border-y border-black/10 py-4 mb-8 text-brand-navy">
          <button onClick={like} disabled={liked} className={`inline-flex items-center gap-2 transition-colors ${liked ? "text-red-500" : "hover:text-brand-primary-bright"}`} data-testid="news-like">
            <Heart size={20} strokeWidth={1.7} fill={liked ? "currentColor" : "none"} />
            <span className="text-sm font-medium">{likeCount}</span>
          </button>
          <a href="#comments" className="inline-flex items-center gap-2 hover:text-brand-primary-bright">
            <MessageCircle size={20} strokeWidth={1.7} />
            <span className="text-sm font-medium">{comments.length}</span>
          </a>
          <button
            onClick={() => {
              if (navigator.share) navigator.share({ title: pick(n.title, ""), url: publicUrl }).catch(() => {});
              else navigator.clipboard?.writeText(publicUrl);
            }}
            className="ms-auto inline-flex items-center gap-2 hover:text-brand-primary-bright"
            aria-label="Paylaş"
            data-testid="news-share"
          >
            <Send size={18} strokeWidth={1.7} />
            <span className="text-sm">Paylaş</span>
          </button>
        </div>

        {/* Source / video */}
        {n.source_name || n.video_url ? (
          <div className="mb-8 flex items-center gap-4 flex-wrap">
            {n.source_name ? (
              n.source_url ? (
                <a href={n.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[0.66rem] tracking-[0.22em] uppercase text-brand-primary hover:text-brand-navy border border-brand-primary/30 hover:border-brand-navy px-3 py-2">
                  Kaynak: {n.source_name} <ExternalLink size={10} strokeWidth={1.5} />
                </a>
              ) : (
                <span className="text-[0.66rem] tracking-[0.22em] uppercase text-brand-medium">Kaynak: {n.source_name}</span>
              )
            ) : null}
            {n.video_url ? (
              <a href={n.video_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-brand-navy text-white px-4 py-2 text-[0.66rem] tracking-[0.22em] uppercase">
                <Play size={12} strokeWidth={1.5} fill="currentColor" /> Videoyu Aç
              </a>
            ) : null}
          </div>
        ) : null}

        {/* Body */}
        <article className="prose prose-lg max-w-none text-brand-dark leading-relaxed whitespace-pre-line">
          {pick(n.body, "")}
        </article>

        {/* Quote */}
        {(n.quote?.[lang] || n.quote?.tr) ? (
          <blockquote className="mt-10 border-s-4 border-brand-primary ps-6 py-2 text-brand-navy font-serif text-2xl leading-snug italic">
            &ldquo;{n.quote?.[lang] || n.quote?.tr}&rdquo;
          </blockquote>
        ) : null}

        {/* Tags */}
        {n.tags && n.tags.length ? (
          <div className="mt-10 flex flex-wrap gap-2">
            {n.tags.map((tag) => (
              <span key={tag} className="text-xs bg-brand-navy/5 text-brand-navy px-3 py-1.5">#{String(tag).replace(/\s+/g, "")}</span>
            ))}
          </div>
        ) : null}

        {/* Comments */}
        <div id="comments" className="mt-14 pt-10 border-t border-black/10" data-testid="news-comments">
          <h2 className="font-serif text-2xl text-brand-navy leading-tight">
            Yorumlar <span className="text-brand-medium text-lg">({comments.length})</span>
          </h2>

          {comments.length ? (
            <ul className="mt-6 space-y-5">
              {comments.map((c) => (
                <li key={c.id || c.created_at} className="border-b border-black/5 pb-5" data-testid={`comment-${c.id || c.created_at}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full bg-brand-navy text-white inline-flex items-center justify-center text-sm font-medium">
                      {(c.name || "?").slice(0, 1).toUpperCase()}
                    </span>
                    <div className="leading-tight">
                      <div className="text-sm font-medium text-brand-navy">{c.name || "Anonim"}</div>
                      <div className="text-[0.66rem] tracking-[0.18em] uppercase text-brand-medium">{formatDate(c.created_at, lang)}</div>
                    </div>
                  </div>
                  <p className="mt-3 text-brand-dark text-sm leading-relaxed whitespace-pre-line">{c.text}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 text-brand-medium text-sm">Henüz yorum yok. İlk yorumu siz yazın.</p>
          )}

          <form onSubmit={submitComment} className="mt-10 bg-brand-light p-6 border border-black/5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[0.66rem] tracking-[0.24em] uppercase text-brand-medium font-medium mb-1.5">Ad Soyad *</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-white border border-black/10 focus:border-brand-primary outline-none px-3 py-2.5 text-sm" data-testid="comment-name" />
              </div>
              <div>
                <label className="block text-[0.66rem] tracking-[0.24em] uppercase text-brand-medium font-medium mb-1.5">E-posta (yayınlanmaz)</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-white border border-black/10 focus:border-brand-primary outline-none px-3 py-2.5 text-sm" data-testid="comment-email" />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-[0.66rem] tracking-[0.24em] uppercase text-brand-medium font-medium mb-1.5">Yorumunuz *</label>
              <textarea required rows={4} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className="w-full bg-white border border-black/10 focus:border-brand-primary outline-none px-3 py-2.5 text-sm" data-testid="comment-text" />
            </div>
            <div className="mt-4 flex items-center justify-between gap-4">
              <p className="text-xs text-brand-medium">Yorumlar onaylandıktan sonra yayınlanır.</p>
              <button type="submit" disabled={submitting} className="btn-primary px-6 py-2.5 text-xs" data-testid="comment-submit">
                {submitting ? "..." : "Yorumu Gönder"}
              </button>
            </div>
          </form>
        </div>

        {/* Back link */}
        <div className="mt-14">
          <Link to="/media" className="link-gold" data-testid="news-back-link">
            <span>← {t("common.viewAll") || "Tüm İçerikler"}</span>
            <ArrowIcon />
          </Link>
        </div>
      </section>
    </div>
  );
};
